import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { usePeople } from "@/lib/hooks/usePeople";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import type { Group, RosterEntry } from "@/types";
import { collator as collatorFor } from "@/i18n";

/**
 * Who is in the group.
 *
 * **No drag to reorder.** A roster has no order that means anything — turn
 * order is decided by the scenario and the casting, not by a list — and a
 * handle that can be dragged is a handle somebody will believe in.
 */
export function RosterTab({ group }: { group: Group }) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const { people } = usePeople();

  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<RosterEntry | undefined>(undefined);
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const onRoster = useMemo(
    () => new Set(group.roster.map((entry) => entry.uid)),
    [group.roster]
  );
  /*
    By name, as the roster itself is. The picker used to be in storage order —
    Jonas, Bruno, Greta, Carla — which is no order at all to somebody looking
    for one child in a list of thirty (live run, 2026-09-09).
  */
  const collator = useMemo(
    () => collatorFor(locale),
    [locale]
  );
  const candidates = people
    .filter((person) => person.role === "student" && !onRoster.has(person.uid) && !person.disabled)
    .sort((a, b) => collator.compare(a.displayName, b.displayName));

  return (
    <div>
      <div className="flex justify-end">
        <Button onClick={() => setAdding(true)}>{t("group.roster.add")}</Button>
      </div>

      {group.roster.length === 0 ? (
        <EmptyState titleKey="group.roster.empty" />
      ) : (
        <ul className="mt-4 divide-y divide-line">
          {group.roster.map((entry) => (
            <li key={entry.uid} className="flex items-center justify-between gap-4 py-3">
              <div>
                <Link
                  to={`/people/${entry.uid}`}
                  className="text-sm text-ink underline-offset-2 hover:underline"
                >
                  {entry.displayName}
                </Link>
                {/* A bare date under a child's name said nothing about itself. */}
                <p className="text-xs text-ink-faint">
                  {t("group.roster.addedOn", { date: formatDate(locale, entry.addedAt) })}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setRemoving(entry)}>
                {t("group.roster.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={adding}
        onClose={() => {
          setAdding(false);
          setChosen([]);
        }}
        titleKey="group.roster.chooseStudents"
      >
        {candidates.length === 0 ? (
          <p className="text-sm text-ink-muted">{t("group.roster.noneToAdd")}</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="max-h-64 overflow-y-auto rounded-card border border-line p-3">
              {candidates.map((person) => (
                <label key={person.uid} className="flex items-center gap-2 py-1 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={chosen.includes(person.uid)}
                    onChange={(event) =>
                      setChosen(
                        event.target.checked
                          ? [...chosen, person.uid]
                          : chosen.filter((uid) => uid !== person.uid)
                      )
                    }
                  />
                  {person.displayName}
                </label>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                busy={busy}
                disabled={chosen.length === 0}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await call(CALLABLES.addToRoster, { groupId: group.id, uids: chosen });
                    success("group.roster.added", { count: chosen.length });
                    setAdding(false);
                    setChosen([]);
                  } catch (error) {
                    showError(error);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("common.add")}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/*
        Removing a student revokes their devices for this group — the backend
        does it, and the dialog says so first. A phone that silently stops
        working in a lesson is a support call nobody can diagnose.
      */}
      <ConfirmDialog
        open={removing !== undefined}
        onClose={() => setRemoving(undefined)}
        onConfirm={async () => {
          if (!removing) return;
          await call(CALLABLES.removeFromRoster, { groupId: group.id, uid: removing.uid });
          success("group.roster.removed", { name: removing.displayName });
        }}
        titleKey="group.roster.removeTitle"
        titleParams={{ name: removing?.displayName ?? "", group: group.name }}
        bodyKey="group.roster.removeBody"
        expected={removing?.displayName ?? ""}
        confirmKey="group.roster.removeConfirm"
        mismatchKey="person.deleteMismatch"
      />
    </div>
  );
}
